import { Box, Flex } from "@chakra-ui/react";
import { ItemStatus, RecurringOption } from "../../types/block/enum";
import { useMemo, useState, useEffect } from "react";
import { meetWithAccountsState } from "../../recoil/account/accountState";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { DEFAULT_SPACE_COLOR } from "../../constants/space";
import useHandleClientBlockStateChange from "../../hooks/block/useHandleClientBlockStateChange";
import { BlockType } from "../../types/block/type";
import { useUpdateCalendarCheckboxMutation } from "../../react-query/calendar/useUpdateCalendarCheckboxMutation";
import { toastState } from "../../recoil/toast/toastState";
import { juneTrack } from "../../utils/june/analytics";
import { JUNE_EVENT } from "../../hooks/june/juneEvent";
import { ToastKind } from "../../types/toast";
import { getToastMessageByKind } from "../../utils/toast/toastUtils";
import { debounceInvalidate } from "../../services/common/debounceInvalidate";
import { calendarQueryKeys } from "../../react-query/calendar/queries";

interface CalendarCheckBoxProps {
  spaceColor: string;
  block: BlockType;
}

export const CalendarCheckBox = ({ spaceColor, block }: CalendarCheckBoxProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPointerFine, setIsPointerFine] = useState(false);
  const [isAnimationActive, setIsAnimationActive] = useState(false);
  const meetWithAccounts = useRecoilValue(meetWithAccountsState);

  const setToast = useSetRecoilState(toastState);

  const { handleClientBasicBlockChange, handleClientRecurringBlockChange } =
    useHandleClientBlockStateChange();
  const updateCalendarCheckbox = useUpdateCalendarCheckboxMutation();

  const isCompleted = block.itemStatus === ItemStatus.COMPLETED;
  const baseColor = useMemo(() => {
    return meetWithAccounts.length === 0 && spaceColor ? spaceColor : DEFAULT_SPACE_COLOR;
  }, [spaceColor, meetWithAccounts]);
  const backgroundColor =
    isCompleted || (isHovered && isPointerFine) ? baseColor : `${baseColor}33`;

  useEffect(() => {
    const mediaQuery = window.matchMedia("(pointer: fine)");
    setIsPointerFine(mediaQuery.matches);

    const handleMediaChange = (e: MediaQueryListEvent) => {
      setIsPointerFine(e.matches);
    };

    mediaQuery.addEventListener("change", handleMediaChange);

    return () => {
      mediaQuery.removeEventListener("change", handleMediaChange);
    };
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const newStatus = isCompleted ? ItemStatus.IN_PROGRESS : ItemStatus.COMPLETED;

    // 체크 표시 상태와 애니메이션 상태를 명확히 구분
    // 체크 완료 상태로 변경 시에만 애니메이션 활성화
    if (newStatus === ItemStatus.COMPLETED) {
      setIsAnimationActive(true); // 체크박스 체크시에는 애니메이션 활성화
    } else {
      setIsAnimationActive(false); // 체크박스 해제시에는 애니메이션 비활성화
    }

    const convertData = {
      allDay: block.allDay,
      hangoutLink: block.hangoutLink,
      meetingCode: block.meetingCode,
      linkData: block.linkData,
      attendees: block.attendees,
      location: block.location,
      priority: block.priority,
      itemStatus: newStatus,
    };
    const isRecurringBlock =
      (block?.recurrence && block?.recurrence?.length > 0) || block.originalId;

    const updateBlock = {
      ...block,
      ...convertData,
    };
    const updatePayload = isRecurringBlock
      ? {
          ...convertData,
          type: RecurringOption.THIS,
          originalStart: block.originalStart ?? block.start,
        }
      : convertData;

    isRecurringBlock
      ? handleClientRecurringBlockChange({
          prevBlockData: block,
          updateBlockData: updateBlock,
        })
      : handleClientBasicBlockChange(updateBlock);

    updateCalendarCheckbox.mutate(
      {
        id: block.id,
        payload: updatePayload,
        prevData: block,
      },
      {
        onSuccess: (data) => {
          setToast({
            isVisible: true,
            message: getToastMessageByKind({ type: ToastKind.DONE, itemStatus: data.itemStatus }),
            type: ToastKind.DONE,
            triggerId: Date.now(),
          });
          juneTrack(
            newStatus === ItemStatus.COMPLETED ? JUNE_EVENT.DONE_TASK : JUNE_EVENT.UNDONE_TASK,
            { location: "calendar" }
          );
        },
        onError: (error) => {
          debounceInvalidate.enqueueInvalidate(calendarQueryKeys.all, { isError: true });
          setToast({
            type: ToastKind.ERROR,
            isVisible: true,
            message: getToastMessageByKind({
              type: ToastKind.ERROR,
              action: isCompleted ? "undo" : "complete",
            }),
            triggerId: Date.now(),
          });
        },
      }
    );
  };

  return (
    <Flex
      py={0.5}
      flexShrink={0}
      id="checkbox-wrapper"
    >
      <Box
        className={`checkbox ${
          isCompleted ? "checkboxCompleted" : ""
        } ${isHovered && isPointerFine && !isCompleted ? "checkboxHovered" : ""} ${isAnimationActive ? "animationActive" : ""}`}
        minW="14px"
        minH="14px"
        borderRadius="3px"
        boxShadow={`0 0 0 1px ${baseColor} inset`}
        position="relative"
        backgroundColor={backgroundColor}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleClick}
        sx={{
          // 체크박스 공통 스타일 - 체크 표시에 대한 기본 스타일
          "&.checkboxCompleted::before": {
            boxSizing: "border-box",
            content: '""',
            position: "absolute",
            top: "35%",
            left: "15%",
            borderRight: "2px solid #1a1c1c",
            borderBottom: "2px solid #1a1c1c",
            transformOrigin: "0% 100%",
            width: "5px",
            height: "9px",
            transform: "translate3d(0, -0.5em, 0) rotate(45deg)",
            display: "block",
          },

          // hover와 pointer: fine인 경우에만 호버 체크마크 표시
          "&:hover:not(.checkboxCompleted)::before": {
            boxSizing: "border-box",
            content: '""',
            position: "absolute",
            top: "35%",
            left: "15%",
            borderRight: "2px solid #1a1c1c",
            borderBottom: "2px solid #1a1c1c",
            transformOrigin: "0% 100%",
            width: "5px",
            height: "9px",
            transform: "translate3d(0, -0.5em, 0) rotate(45deg)",
            // 모바일(pointer: coarse)에서는 호버 체크 아이콘 숨기기
            "@media (pointer: coarse)": {
              display: "none !important",
            },
          },

          // pointer: coarse일 때 hover 오버라이드
          "@media (pointer: coarse)": {
            "&:hover:not(.checkboxCompleted)::before": {
              display: "none !important",
            },
          },

          // completed & animation 상태일 때 박스 애니메이션
          "&.checkboxCompleted.animationActive": {
            animation: "shrink-bounce 200ms cubic-bezier(0.4, 0, 0.23, 1) forwards",
          },

          // completed & animation 상태일 때 체크마크 애니메이션
          "&.checkboxCompleted.animationActive::before": {
            boxSizing: "border-box",
            content: '""',
            position: "absolute",
            top: "35%",
            left: "15%",
            borderRight: "2px solid transparent",
            borderBottom: "2px solid transparent",
            transform: "rotate(45deg)",
            transformOrigin: "0% 100%",
            display: "block !important", // 모든 환경에서 애니메이션은 표시
            animation: "checkbox-check 125ms 250ms cubic-bezier(0.4, 0, 0.23, 1) forwards",
          },
          "@keyframes shrink-bounce": {
            "0%": { transform: "scale(1)" },
            "33%": { transform: "scale(0.85)" },
            "100%": { transform: "scale(1)" },
          },
          "@keyframes checkbox-check": {
            "0%": {
              width: 0,
              height: 0,
              borderColor: "#1a1c1c",
              transform: "translate3d(0, 0, 0) rotate(45deg)",
            },
            "33%": {
              width: "5px",
              height: 0,
              transform: "translate3d(0, 0, 0) rotate(45deg)",
            },
            "100%": {
              width: "5px",
              height: "9px",
              borderColor: "#1a1c1c",
              transform: "translate3d(0, -0.5em, 0) rotate(45deg)",
            },
          },
        }}
      />
    </Flex>
  );
};
